import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FloppyDisk, Plus, X, Upload, FileArrowDown, UserCircle, SealCheck, MapPin, LinkSimple, GraduationCap, Certificate, Briefcase } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { careerApi, AVAILABILITY_STATES } from "@/lib/jobsApi";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { phoneError } from "@/lib/phone";

/**
 * Career profile. Two modes:
 *   - Editor at /career (self, editable)
 *   - Public view at /career/:userId (read-only when viewing others)
 */
export default function CareerProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPublic = Boolean(userId) && userId !== user?.user_id;
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");  // kind currently uploading

  useEffect(() => {
    const q = isPublic ? careerApi.publicProfile(userId) : careerApi.me();
    q.then(setP).catch(() => { toast.error("Profile not available"); navigate("/jobs"); });
  }, [isPublic, userId, navigate]);

  const set = (k, v) => setP({ ...p, [k]: v });

  const save = async () => {
    if (p?.contact_phone && phoneError(p.contact_phone)) {
      toast.error(phoneError(p.contact_phone));
      return;
    }
    setSaving(true);
    try {
      const saved = await careerApi.update({
        headline: p.headline || "",
        summary: p.summary || "",
        photo: p.photo || "",
        education: p.education || [],
        certifications: p.certifications || [],
        experience: p.experience || [],
        skills: p.skills || [],
        languages: p.languages || [],
        preferred_job_types: p.preferred_job_types || [],
        preferred_locations: p.preferred_locations || [],
        availability: p.availability || "open",
        contact_email: p.contact_email || "",
        contact_phone: p.contact_phone || "",
        privacy: p.privacy || "public",
        visible: p.visible !== false,
      });
      setP(saved);
      toast.success("Profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const upload = async (file, kind) => {
    if (!file) return;
    setUploading(kind);
    try {
      const res = await careerApi.upload(file, kind);
      if (kind === "cv") {
        setP({ ...p, cv_url: res.url, cv_filename: res.filename });
      } else if (kind === "portfolio") {
        setP({ ...p, portfolio_url: res.url });
      } else if (kind === "video") {
        setP({ ...p, video_intro_url: res.url });
      }
      toast.success(`${kind === "cv" ? "CV" : kind} uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(""); }
  };

  if (!p) return <div className="p-8 text-white/50">Loading…</div>;

  if (isPublic) return <PublicView p={p} onMessage={null} navigate={navigate} />;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-4" data-testid="career-page">
      <button onClick={() => navigate("/jobs")} className="text-white/60 hover:text-white flex items-center gap-1 text-sm">
        <ArrowLeft size={14} weight="bold" /> Back to Jobs
      </button>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading text-3xl md:text-4xl font-black">My career profile</h1>
        <Button data-testid="career-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
          <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Basics</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Field label="Professional headline">
            <Input data-testid="career-headline" value={p.headline || ""} onChange={(e) => set("headline", e.target.value)} placeholder="e.g. Full-Stack Engineer · React + FastAPI" className="bg-white/5 border-white/10" />
          </Field>
          <Field label="Short summary">
            <Textarea data-testid="career-summary" value={p.summary || ""} onChange={(e) => set("summary", e.target.value)} rows={4} placeholder="2-3 sentences on what you do best" className="bg-white/5 border-white/10" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Contact email">
              <Input data-testid="career-email" type="email" value={p.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Phone (optional — must start with +country code)">
              <Input
                data-testid="career-phone"
                value={p.contact_phone || ""}
                onChange={(e) => {
                  let v = e.target.value;
                  if (v && !v.startsWith("+")) v = "+" + v.replace(/^\++/, "");
                  set("contact_phone", v);
                }}
                placeholder="+233 XX XXX XXXX"
                className="bg-white/5 border-white/10"
              />
              {p.contact_phone && phoneError(p.contact_phone) && (
                <p className="text-[11px] text-red-400 mt-1" data-testid="career-phone-error">{phoneError(p.contact_phone)}</p>
              )}
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Availability">
              <Select value={p.availability || "open"} onValueChange={(v) => set("availability", v)}>
                <SelectTrigger data-testid="career-availability" className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                  {AVAILABILITY_STATES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Privacy">
              <Select value={p.privacy || "public"} onValueChange={(v) => set("privacy", v)}>
                <SelectTrigger data-testid="career-privacy" className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                  <SelectItem value="public">Public — anyone can view</SelectItem>
                  <SelectItem value="employers_only">Only employers browsing</SelectItem>
                  <SelectItem value="private">Private (hidden)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Documents</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <FileTile label="CV / Resume" accept=".pdf,.doc,.docx" file={p.cv_url} filename={p.cv_filename} uploading={uploading === "cv"} onUpload={(f) => upload(f, "cv")} onClear={() => setP({ ...p, cv_url: null, cv_filename: null })} testId="career-cv" />
          <FileTile label="Portfolio" accept=".pdf,.zip,image/*" file={p.portfolio_url} uploading={uploading === "portfolio"} onUpload={(f) => upload(f, "portfolio")} onClear={() => setP({ ...p, portfolio_url: null })} testId="career-portfolio" />
          <FileTile label="Video intro (mp4)" accept="video/mp4,video/webm" file={p.video_intro_url} uploading={uploading === "video"} onUpload={(f) => upload(f, "video")} onClear={() => setP({ ...p, video_intro_url: null })} testId="career-video" />
        </CardContent>
      </Card>

      <ListSection
        title="Skills"
        items={p.skills || []}
        onAdd={(v) => set("skills", [...(p.skills || []), v])}
        onRemove={(i) => set("skills", p.skills.filter((_, idx) => idx !== i))}
        placeholder="e.g. React"
        testIdPrefix="skill"
      />
      <ListSection
        title="Languages"
        items={p.languages || []}
        onAdd={(v) => set("languages", [...(p.languages || []), v])}
        onRemove={(i) => set("languages", p.languages.filter((_, idx) => idx !== i))}
        placeholder="e.g. English"
        testIdPrefix="lang"
      />

      <MultiEntrySection
        title="Work experience" icon={Briefcase}
        items={p.experience || []}
        empty={{ company: "", title: "", start: "", end: "", description: "", current: false }}
        fields={[
          { key: "company", label: "Company", placeholder: "Company name" },
          { key: "title", label: "Title", placeholder: "Role" },
          { key: "start", label: "Start", type: "month" },
          { key: "end", label: "End", type: "month", placeholder: "or leave empty" },
          { key: "description", label: "What you did", multiline: true },
        ]}
        onChange={(v) => set("experience", v)}
        testIdPrefix="career-exp"
      />
      <MultiEntrySection
        title="Education" icon={GraduationCap}
        items={p.education || []}
        empty={{ school: "", degree: "", field: "", start: "", end: "" }}
        fields={[
          { key: "school", label: "School" },
          { key: "degree", label: "Degree" },
          { key: "field", label: "Field of study" },
          { key: "start", label: "Start", type: "month" },
          { key: "end", label: "End", type: "month" },
        ]}
        onChange={(v) => set("education", v)}
        testIdPrefix="career-edu"
      />
      <MultiEntrySection
        title="Certifications" icon={Certificate}
        items={p.certifications || []}
        empty={{ name: "", issuer: "", date: "", url: "" }}
        fields={[
          { key: "name", label: "Certification" },
          { key: "issuer", label: "Issuer" },
          { key: "date", label: "Date", type: "month" },
          { key: "url", label: "Verification URL" },
        ]}
        onChange={(v) => set("certifications", v)}
        testIdPrefix="career-cert"
      />

      <Button data-testid="career-save-bottom" onClick={save} disabled={saving} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-heading font-black gap-2">
        <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : "Save profile"}
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/50 font-bold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function FileTile({ label, accept, file, filename, uploading, onUpload, onClear, testId }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
      <p className="text-xs uppercase tracking-widest text-white/50 font-bold">{label}</p>
      {file ? (
        <div className="space-y-2">
          <a href={file} target="_blank" rel="noreferrer" className="text-blue-300 hover:underline text-sm break-all inline-flex items-center gap-1">
            <FileArrowDown size={14} weight="bold" /> {filename || "View file"}
          </a>
          <div className="flex gap-2">
            <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded border border-white/15 text-xs hover:bg-white/5">
              <Upload size={12} weight="bold" /> Replace
              <input data-testid={`${testId}-upload`} type="file" accept={accept} className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
            </label>
            <button onClick={onClear} className="text-red-300 text-xs hover:underline">Remove</button>
          </div>
        </div>
      ) : (
        <label className="cursor-pointer flex flex-col items-center justify-center py-6 rounded border border-dashed border-white/20 hover:bg-white/5 text-sm text-white/60">
          <Upload size={20} weight="bold" className="mb-1" />
          {uploading ? "Uploading…" : `Upload ${label}`}
          <input data-testid={`${testId}-upload`} type="file" accept={accept} className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
        </label>
      )}
    </div>
  );
}

function ListSection({ title, items, onAdd, onRemove, placeholder, testIdPrefix }) {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onAdd(v.trim()); setV(""); } };
  return (
    <Card className="bg-white/[0.03] border-white/10">
      <CardHeader><CardTitle className="font-heading">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input data-testid={`${testIdPrefix}-input`} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} className="bg-white/5 border-white/10" />
          <Button data-testid={`${testIdPrefix}-add`} type="button" onClick={add} variant="outline" className="border-white/20 bg-transparent hover:bg-white/5 gap-1"><Plus size={14} weight="bold" /> Add</Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <span key={`${it}-${i}`} data-testid={`${testIdPrefix}-tag-${i}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-200 text-xs font-heading font-bold">
              {it}
              <button data-testid={`${testIdPrefix}-remove-${i}`} onClick={() => onRemove(i)} className="hover:text-red-300"><X size={11} weight="bold" /></button>
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function MultiEntrySection({ title, icon: Icon, items, empty, fields, onChange, testIdPrefix }) {
  const add = () => onChange([...items, { ...empty }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const edit = (i, k, v) => onChange(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  return (
    <Card className="bg-white/[0.03] border-white/10">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="font-heading flex items-center gap-2">
          {Icon && <Icon size={18} weight="fill" />} {title}
        </CardTitle>
        <Button data-testid={`${testIdPrefix}-add`} onClick={add} size="sm" variant="outline" className="border-white/20 bg-transparent hover:bg-white/5 gap-1"><Plus size={12} weight="bold" /> Add</Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {items.length === 0 && <p className="text-white/40 text-sm">Nothing added yet.</p>}
        {items.map((it, i) => (
          <div key={i} data-testid={`${testIdPrefix}-${i}`} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2 relative">
            <button data-testid={`${testIdPrefix}-remove-${i}`} onClick={() => remove(i)} className="absolute top-2 right-2 text-white/40 hover:text-red-300"><X size={14} weight="bold" /></button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {fields.map((f) => (
                <div key={f.key} className={f.multiline ? "md:col-span-2" : ""}>
                  <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{f.label}</label>
                  {f.multiline ? (
                    <Textarea data-testid={`${testIdPrefix}-${i}-${f.key}`} value={it[f.key] || ""} onChange={(e) => edit(i, f.key, e.target.value)} rows={2} className="bg-white/5 border-white/10" />
                  ) : (
                    <Input data-testid={`${testIdPrefix}-${i}-${f.key}`} type={f.type || "text"} value={it[f.key] || ""} onChange={(e) => edit(i, f.key, e.target.value)} placeholder={f.placeholder} className="bg-white/5 border-white/10" />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/* -------------------- Public view -------------------- */
function PublicView({ p, navigate }) {
  const u = p.user || {};
  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-4" data-testid="public-career-page">
      <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white flex items-center gap-1 text-sm">
        <ArrowLeft size={14} weight="bold" /> Back
      </button>
      <div className="flex items-start gap-4">
        {p.photo || u.picture ? (
          <img src={p.photo || u.picture} alt="" className="w-24 h-24 rounded-full object-cover" />
        ) : (
          <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center">
            <UserCircle size={48} className="text-white/40" weight="duotone" />
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-3xl font-black">{u.name || "Candidate"}</h1>
            {u.employer_verified && <SealCheck size={22} className="text-blue-400" weight="fill" />}
          </div>
          {p.headline && <p className="text-white/70">{p.headline}</p>}
          <div className="flex flex-wrap gap-2 mt-2 text-xs text-white/60">
            {p.availability && <span className="px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">{AVAILABILITY_STATES.find((a) => a.value === p.availability)?.label}</span>}
            {p.preferred_locations?.length > 0 && <span className="inline-flex items-center gap-1"><MapPin size={11} weight="bold" /> {p.preferred_locations.join(", ")}</span>}
          </div>
        </div>
      </div>

      {p.summary && <p className="text-white/80 whitespace-pre-line leading-relaxed">{p.summary}</p>}

      {p.skills?.length > 0 && (
        <Section title="Skills">
          <div className="flex flex-wrap gap-1.5">
            {p.skills.map((s) => <span key={s} className="px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-200 text-xs font-heading font-bold">{s}</span>)}
          </div>
        </Section>
      )}
      {p.experience?.length > 0 && (
        <Section title="Experience">
          <div className="space-y-3">
            {p.experience.map((e, i) => (
              <div key={i} className="border-l-2 border-blue-500/40 pl-3">
                <div className="font-heading font-bold">{e.title} <span className="text-white/50 font-normal">at {e.company}</span></div>
                <div className="text-xs text-white/50">{e.start} – {e.end || "present"}</div>
                {e.description && <p className="text-sm text-white/70 mt-1 whitespace-pre-line">{e.description}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}
      {p.education?.length > 0 && (
        <Section title="Education">
          {p.education.map((ed, i) => (
            <div key={i} className="mt-1"><span className="font-bold">{ed.degree || ""}</span> {ed.field && `in ${ed.field}`} · {ed.school} <span className="text-white/50 text-xs">({ed.start} – {ed.end || "present"})</span></div>
          ))}
        </Section>
      )}
      {p.certifications?.length > 0 && (
        <Section title="Certifications">
          {p.certifications.map((c, i) => (
            <div key={i} className="mt-1">{c.name} {c.issuer && `· ${c.issuer}`} {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="text-blue-300 hover:underline text-xs inline-flex items-center gap-1"><LinkSimple size={11} /> verify</a>}</div>
          ))}
        </Section>
      )}
      {p.cv_url && (
        <a href={p.cv_url} target="_blank" rel="noreferrer" data-testid="public-cv" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-heading font-bold">
          <FileArrowDown size={16} weight="bold" /> Download CV
        </a>
      )}
      {p.video_intro_url && (
        <Section title="Video intro">
          <video src={p.video_intro_url} controls playsInline className="w-full max-w-md rounded-lg" />
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-heading text-sm uppercase tracking-widest text-white/50 font-black">{title}</h2>
      <div className="mt-2 text-white/85">{children}</div>
    </div>
  );
}
